import { useState, useEffect, useCallback } from 'react';
import { useList, useGetIdentity } from '@refinedev/core';
import { Loader2, Check, X } from 'lucide-react';
import { notify } from '@/lib/notify';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface AdminUserOption {
  id: number;
  username: string;
  email: string;
}

interface UserIdentity {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface SessionShare {
  sharedWithAdminId: number;
  username: string;
  email: string;
  createdAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface ShareSessionDialogProps {
  sessionId: number;
  sessionName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShareSessionDialog({ sessionId, sessionName, open, onOpenChange }: ShareSessionDialogProps) {
  const { data: identity } = useGetIdentity<UserIdentity>();
  const [shares, setShares] = useState<SessionShare[]>([]);
  const [isLoadingShares, setIsLoadingShares] = useState(false);
  const [busyAdminId, setBusyAdminId] = useState<number | null>(null);

  const adminsResult = useList<AdminUserOption>({
    resource: 'admin-users',
    pagination: { mode: 'off' },
  });
  const admins = adminsResult.result?.data ?? [];

  const fetchShares = useCallback(async () => {
    setIsLoadingShares(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/accounts/${sessionId}/shares`);
      const result: ApiResponse<SessionShare[]> = await response.json();
      if (result.success && result.data) {
        setShares(result.data);
      } else {
        setShares([]);
      }
    } catch {
      setShares([]);
    } finally {
      setIsLoadingShares(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (open) {
      fetchShares();
    }
  }, [open, fetchShares]);

  const sharedIds = new Set(shares.map((s) => s.sharedWithAdminId));

  const handleShare = async (adminUserId: number) => {
    setBusyAdminId(adminUserId);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/accounts/${sessionId}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminUserId }),
      });
      const result: ApiResponse<null> = await response.json();
      if (!result.success) {
        notify.error(result.error || 'Не удалось предоставить доступ');
        return;
      }
      notify.success('Доступ предоставлен');
      await fetchShares();
    } catch {
      notify.error('Ошибка сети');
    } finally {
      setBusyAdminId(null);
    }
  };

  const handleUnshare = async (adminUserId: number) => {
    setBusyAdminId(adminUserId);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/accounts/${sessionId}/share/${adminUserId}`, {
        method: 'DELETE',
      });
      const result: ApiResponse<null> = await response.json();
      if (!result.success) {
        notify.error(result.error || 'Не удалось снять доступ');
        return;
      }
      notify.success('Доступ снят');
      await fetchShares();
    } catch {
      notify.error('Ошибка сети');
    } finally {
      setBusyAdminId(null);
    }
  };

  // Исключаем себя — нельзя поделиться сессией с самим собой
  const selectableAdmins = admins.filter((a) => a.id !== identity?.id);
  const isLoading = adminsResult.query.isLoading || isLoadingShares;

  const renderAdminRow = (admin: AdminUserOption) => {
    const isShared = sharedIds.has(admin.id);
    const isBusy = busyAdminId === admin.id;
    return (
      <li
        key={admin.id}
        className="flex items-center justify-between gap-2 rounded-md px-2 py-2 hover:bg-accent"
      >
        <div className="flex flex-col min-w-0">
          <span className="truncate text-sm">{admin.username}</span>
          <span className="truncate text-xs text-muted-foreground">{admin.email}</span>
        </div>
        {isShared ? (
          <Button
            variant="ghost"
            size="sm"
            className="flex-shrink-0 text-destructive hover:text-destructive"
            disabled={isBusy}
            onClick={() => handleUnshare(admin.id)}
          >
            {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="mr-1 h-4 w-4" />}
            {!isBusy && 'Убрать'}
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="flex-shrink-0"
            disabled={isBusy}
            onClick={() => handleShare(admin.id)}
          >
            {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}
            {!isBusy && 'Дать доступ'}
          </Button>
        )}
      </li>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (selectableAdmins.length === 0) {
      return (
        <div className="py-8 text-center text-sm text-muted-foreground">
          Нет других сотрудников
        </div>
      );
    }
    return <ul className="space-y-1">{selectableAdmins.map(renderAdminRow)}</ul>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Доступ к сессии</DialogTitle>
          <DialogDescription>
            Сотрудники с доступом к <span className="font-medium">{sessionName}</span> смогут
            выбрать эту Telegram-сессию для приглашений, рассылок и чатов.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-80 overflow-y-auto -mx-1 px-1">
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
